import React, { memo, useState, useEffect, Fragment, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  styled,
  keyframes,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  YouTube as YouTubeIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  Play,
  CheckCircle2,
  Circle,
  Loader2,
  ArrowLeft,
  Upload,
  AlertCircle,
  Pause,
  StopCircle,
  SquareStop,
} from "lucide-react";

// Components
import MeGenBreadcrumbs, { BreadcrumbItem } from "./common/MeGenBreadcrumbs";
import ModMeGenAiUsage from "./common/ModMeGenAiUsage";
import AiUsageButton, { FetchAiUsage } from "./common/AiUsageButton";
import CardGenerateVideoThumbnail from "./CardGenerateVideoThumbnail";
import CardGenerateVideoAssets from "./CardGenerateVideoAssets";
import CardGenerateVideoFinalVideo from "./CardGenerateVideoFinalVideo";

// Hooks
import useRunPolling from "./useRunPolling";

// Interfaces
import { getStaticUrlNews } from "./MeGenInterface";
import { STOPPED_BY_USER, firstStoppedStep, isStoppedByUser } from "./MeGenRunStatus";

// Utils
import moment from "moment";
import { hydrateVoicePrompt, synchronizeVoicePrompt } from "./common/voicePrompt";

// Types
import { PollingEvent } from "./useRunPolling";
import { Event } from "./MeGenInterface";
import { RunStateSerializer, Steps, UpdateItemPayload, UpdateMetadataPayload, SeriesOption, UpdateCompositionPayload, YouTubeChannel } from "./types/MeGenTypes";

export type CardGenerateVideoProps = {
  projectId: number;
  projectName: string;
  seriesId?: number;
  seriesName?: string;
  seriesList?: SeriesOption[];
  threadId: string | null;
  canViewAiUsage?: boolean;
  onFetchAiUsage?: FetchAiUsage;
  aiUsageUserNameMap?: Record<string, string>;
  isDryRun?: boolean;
  isRunning?: boolean;
  // Connection
  youtubeChannels?: YouTubeChannel[];
  // Callbacks
  onNavigateBack?: () => void;
  onStartRun?: (dryRun: boolean, seriesId: number | null) => void;
  onStopRun?: () => void;
  onResumeUpload?: (threadId: string, credentialId: number | null) => void;
  onRerun?: (treadId: string) => void;
  onUpdateItem?: (threadId: string, itemId: string, data: any, onSuccess?: (newState: RunStateSerializer, isGenerating: boolean) => void) => void;
  onUpdateMetadata?: (threadId: string, data: any, onSuccess?: (newState: RunStateSerializer, isGenerating: boolean) => void) => void;
  onUpdateComposition?: (threadId: string, data: UpdateCompositionPayload, onSuccess?: (newState: RunStateSerializer, isGenerating: boolean) => void) => void,
  onGeneratingChange?: (isGenerating: boolean) => void;
  // Polling
  onEvent?: (e: Event) => void;
};

type StepItem = {
  id: Steps;
  label: string;
};

type StepStatus = "pending" | "active" | "completed" | "error" | "stopped";

const STEPS: StepItem[] = [
  { id: "load_data", label: "Load Data" },
  { id: "process_items", label: "Process Items (AI)" },
  { id: "generate_assets", label: "Generate Assets" },
  { id: "merge_media", label: "Merge Media (FFmpeg)" },
  { id: "prepare_youtube_metadata", label: "Prepare Metadata" },
  { id: "create_thumbnail", label: "Create Thumbnail" },
  { id: "upload_to_youtube", label: "Upload to YouTube" },
];

// ============ Keyframes ============
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============ Styled Components ============

const Container = styled(Box)({ padding: "0 10px" });

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: "0.75rem",
  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  border: "1px solid #e2e8f0",
  padding: "1.5rem",
  marginBottom: "1.5rem",
});

const Header = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "1.5rem",
});

const HeaderLeft = styled(Box)({ display: "flex", alignItems: "center", gap: "0.5rem" });
const HeaderRight = styled(Box)({ display: "flex", alignItems: "center", gap: "1rem" });

const Title = styled(Typography)({ fontSize: "1.125rem", fontWeight: 600, color: "#1e293b" });
const Subtitle = styled(Typography)({ fontSize: "0.875rem", color: "#64748b" });

const BackButton = styled(IconButton)({ padding: "0.25rem", color: "#64748b" });

const PrimaryButton = styled(Button)({
  backgroundColor: "#16a34a", color: "white", padding: "0.5rem 1.5rem",
  borderRadius: "9999px", textTransform: "none", fontWeight: 500,
  boxShadow: "0 10px 15px -3px rgba(22, 163, 74, 0.2)",
  "&:hover": { backgroundColor: "#15803d" },
});

const StepByStepButton = styled(Button)({
  backgroundColor: "#ffae00ff", color: "white", padding: "0.5rem 1.5rem",
  borderRadius: "9999px", textTransform: "none", fontWeight: 500,
  boxShadow: "0 10px 15px -3px rgba(22, 163, 74, 0.2)",
  "&:hover": { backgroundColor: "#c28501ff" },
});

const DangerButton = styled(Button)({
  backgroundColor: "#dc2626", color: "white", padding: "0.5rem 1.5rem",
  borderRadius: "9999px", textTransform: "none", fontWeight: 500,
  boxShadow: "0 10px 15px -3px rgba(220, 38, 38, 0.2)",
  "&:hover": { backgroundColor: "#b91c1c" },
});

const StopButton = styled(IconButton)({
  padding: "0.375rem",
  color: "#dc2626",
  backgroundColor: "#fee2e2",
  border: "1px solid #fecaca",
  borderRadius: "0.375rem",
  "&:hover": { backgroundColor: "#fecaca" },
});

const CheckboxLabel = styled(FormControlLabel)({
  "& .MuiFormControlLabel-label": { fontSize: "0.875rem", fontWeight: 500, color: "#1e293b" },
});

const StepContainer = styled(Box)({
  position: "relative", display: "flex", alignItems: "center", gap: "1rem",
  cursor: "pointer", marginBottom: "1rem",
});

const StepIndicator = styled(Box)<{ status: string }>(({ status }) => ({
  width: "2rem", height: "2rem", borderRadius: "50%",
  display: "flex", alignItems: "center", justifyContent: "center",
  border: "2px solid", zIndex: 10, transition: "all 0.2s",
  backgroundColor: 
    status === "completed" ? "#dcfce7" : 
    status === "active" ? "#dbeafe" : 
    status === "error" ? "#fee2e2" : 
    status === "stopped" ? "#fef3c7" :
    "#f8fafc",
  borderColor: 
    status === "completed" ? "#22c55e" : 
    status === "active" ? "#3b82f6" : 
    status === "error" ? "#ef4444" : 
    status === "stopped" ? "#f59e0b" :
    "#e2e8f0",
  color: 
    status === "completed" ? "#16a34a" : 
    status === "active" ? "#2563eb" : 
    status === "error" ? "#ef4444" : 
    status === "stopped" ? "#d97706" :
    "#cbd5e1",
}));

const SpinningLoader = styled(Loader2)({
  animation: `${spin} 1s linear infinite`,
});

const StepContent = styled(Box)({
  flex: 1, backgroundColor: "#f8fafc", borderRadius: "0.5rem", padding: "0.75rem",
  border: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center",
});

const StepLabel = styled(Typography)<{ status: string }>(({ status }) => ({
  fontWeight: 500,
  color: 
    status === "completed" ? "#334155" : 
    status === "active" ? "#1d4ed8" : 
    status === "error" ? "#dc2626" : 
    status === "stopped" ? "#b45309" :
    "#94a3b8",
}));

const Badge = styled("span")<{ variant: string }>(({ variant }) => ({
  fontSize: "0.75rem", padding: "0.25rem 0.5rem", borderRadius: "9999px",
  backgroundColor: 
    variant === "success" ? "#dcfce7" : 
    variant === "info" ? "#dbeafe" : 
    variant === "warning" ? "#fef3c7" : 
    variant === "error" ? "#fee2e2" : 
    variant === "stopped" ? "#fef3c7" :
    "#f1f5f9",
  color: 
    variant === "success" ? "#15803d" : 
    variant === "info" ? "#1d4ed8" : 
    variant === "warning" ? "#b45309" : 
    variant === "error" ? "#dc2626" : 
    variant === "stopped" ? "#b45309" :
    "#64748b",
}));

const ConnectingLine = styled(Box)({
  position: "absolute", left: "15px", top: "2.5rem", width: "2px", height: "1.5rem", backgroundColor: "#f1f5f9",
});

const ProgressDetail = styled(Box)({
  marginLeft: "3rem", marginTop: "0.5rem", padding: "0.75rem",
  backgroundColor: "#f8fafc", borderRadius: "0.5rem", border: "1px solid #f1f5f9",
  fontSize: "0.875rem", marginBottom: "1rem",
});

const TerminalCard = styled(Box)({
  backgroundColor: "#0f172a", borderRadius: "0.75rem",
  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)", padding: "1.5rem",
  fontFamily: "monospace", fontSize: "0.875rem", color: "#cbd5e1", overflow: "hidden",
});

const TerminalHeader = styled(Box)({
  display: "flex", alignItems: "center", gap: "0.5rem", color: "#64748b",
  paddingBottom: "0.5rem", borderBottom: "1px solid #1e293b", marginBottom: "1rem",
});

const TerminalDot = styled(Box)<{ color: string }>(({ color }) => ({
  width: "0.75rem", height: "0.75rem", borderRadius: "50%", backgroundColor: color,
}));

const RunningIndicator = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  color: "#2563eb",
});

const SeriesSelect = styled(FormControl)({
  minWidth: 200,
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
  },
  "& .MuiSelect-select": {
    fontSize: "0.875rem",
    padding: "0.5rem 1rem",
  },
})

// ============ Component ============

const CardGenerateVideo: React.FC<CardGenerateVideoProps> = (props) => {
  const initialSeriesId = props.seriesId;
  const seriesList = props.seriesList ?? [];
  const initialDryRun = props.isDryRun ?? true;
  const externalIsRunning = props.isRunning ?? false;
  const youtubeChannels = props.youtubeChannels ?? [];

  const [isProgressExpanded, setIsProgressExpanded] = useState<boolean>(true);
  const [isDryRun, setIsDryRun] = useState(initialDryRun);
  const [selectedSeriesId, setSelectedSeriesId] = useState<number | "">(initialSeriesId || "");
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Omit<UpdateItemPayload, "item_id">>({});
  // local state
  const [graphState, setGraphState] = useState<RunStateSerializer | undefined>(undefined);
  // connection state
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  // Timer for elapsed time
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const isRunning = externalIsRunning;
  const isViewingRun = !!props.threadId;

  const handlePollingSuccess = useCallback(
    (newState: RunStateSerializer, isGenerating: boolean) => {
      setGraphState((prev) => {
        if (prev === newState) return prev;

        if (prev && JSON.stringify(prev) === JSON.stringify(newState)) return prev;
        return newState;
      });

      props.onGeneratingChange?.(isGenerating);
    },
    [props.onGeneratingChange]
  );

  useRunPolling({
    threadId: props.threadId,
    isGenerating: isRunning,
    eventMessage: "HandleGetRunState",
    onEvent: props.onEvent! as (e: PollingEvent) => void,
    onSuccess: handlePollingSuccess,
  });

  const status = graphState;

  // ผู้ใช้กดหยุด — ดู 2 ระดับ: graph state (มาหลังกราฟรับรู้ cancel) และ progress ของ step (POST /stop เขียนทันที)
  const errMessage = status?.values?.error ?? "";
  const stoppedStep = firstStoppedStep(status?.progress);
  const isStopped = isStoppedByUser(errMessage) || stoppedStep !== null;

  const isError = ["Err", "Missing"].some(keyword => errMessage.includes(keyword));

  // Memo
  const mediaVersion = useMemo(() => {
    return moment(status?.created_at).format("DDMMYYYY_HHmmss") || "";
  }, [status?.created_at]);

  const assetsMediaVersion = useMemo(() => {
    const finishedAt = status?.progress?.process_items?.finished_at;
    return finishedAt ? moment(finishedAt).format("DDMMYYYY_HHmmss") : mediaVersion;
  }, [status?.progress?.process_items?.finished_at, mediaVersion]);

  const isAssetsRunning = useMemo(() => {
    const processItemsDone = status?.progress?.process_items?.status === "done";
    const generateAssetsDone = status?.progress?.generate_assets?.status === "done";

    return !(processItemsDone && generateAssetsDone && !isStopped);
  }, [
    status?.progress?.process_items?.status,
    status?.progress?.generate_assets?.status,
    isStopped,
  ]);

  const thumbnailData = useMemo(() => ({
    thumbnailPath: status?.values?.thumbnail_path || "",
    youtubeTitle: status?.values?.youtube_title || "",
    youtubeDescription: status?.values?.youtube_description || "",
    thumbnailPrompt: status?.values?.thumbnail_prompt || "",
    thumbnailDate: status?.values?.thumbnail_date || "",
  }), [
    status?.values?.thumbnail_path,
    status?.values?.youtube_title,
    status?.values?.youtube_description,
    status?.values?.thumbnail_prompt,
    status?.values?.thumbnail_date,
  ]);

  const statbleItems = useMemo(() => {
    return status?.values?.items || [];
  }, [JSON.stringify(status?.values?.items)]);

  const finalVideoUrl = useMemo(() => {
    const rawFinalVideoUrl = status?.values?.final_video_path ? getStaticUrlNews(status.values.final_video_path) : "";
    const finalVideoUrl = rawFinalVideoUrl ? `${rawFinalVideoUrl}?v=${mediaVersion}` : "";

    return finalVideoUrl;
  }, [status?.values?.final_video_path, mediaVersion]);

  const handleSaveMetadata = useCallback((data: UpdateMetadataPayload) => {
    if (!props.threadId) return;
    props.onUpdateMetadata?.(props.threadId, data, handlePollingSuccess);
  }, [props.threadId, props.onUpdateMetadata]);

  const handleSaveComposition = useCallback((
    threadId: string,
    data: UpdateCompositionPayload,
    onSuccess?: () => void
  ) => {
    props.onUpdateComposition?.(threadId, data, handlePollingSuccess);

    if (onSuccess) {
      onSuccess();
    }
  }, [props.threadId, props.onUpdateComposition]);

  // Breadcrumbs
  const [isAiUsageOpen, setIsAiUsageOpen] = useState(false);

  const runName = useMemo(() => {
    const youtubeTitle = status?.values?.youtube_title;
    const genTitle = props.threadId ? `Episode ${props.threadId.slice(0, 8).toUpperCase()}` : "Run Details";

    return youtubeTitle || genTitle;
  }, [status?.values, props.threadId]);
  
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: props.projectName, path: `/project/${props.projectId}`},
    { label: isViewingRun ? runName : "New Workflow Run" },
  ];

  // Helpers
  const stepIds: string[] = STEPS.map((s) => s.id);

  const getStepStatus = (stepId: Steps): StepStatus => {
    if (!status) return "pending";

    // ผู้ใช้กดหยุด — ต้องมาก่อนสาขา error ข้างล่าง ไม่งั้น progress.<step>.error = "Stopped by user" จะถูกนับเป็น error แดง
    // step ที่หยุดเอาจาก progress (POST /stop เขียนลง step ที่กำลังวิ่ง) · fallback next[0] เฉพาะ state ที่ไม่มี progress
    // ห้ามเชื่อ next[0] เป็นหลัก: หลัง stop กราฟ short-circuit ไปค้างที่ interrupt ก่อน upload_to_youtube ⇒ next = ["upload_to_youtube"]
    if (isStopped) {
      const stoppedIndex = stepIds.indexOf(stoppedStep ?? status.next[0]);
      const currentIndex = stepIds.indexOf(stepId);

      if (stoppedIndex !== -1 && currentIndex < stoppedIndex) return "completed";
      if (currentIndex === stoppedIndex) return "stopped";
      return "pending";
    }

    // Check for error in progress
    if (status.progress) {
      // shadow ตัวนอกโดยตั้งใจ: ตรงนี้ต้องเป็น Steps[] เพื่อ index เข้า status.progress (RunStateProgress ไม่ใช่ Record<string, ...>)
      const stepIds = STEPS.map((s) => s.id);
      const failedStepId = stepIds.find(
        (id) => 
          status.progress?.[id]?.status === "error" ||
          (status.progress?.[id]?.error && status.progress?.[id]?.error !== "")
      );

      if (failedStepId) {
        const failedIndex = stepIds.indexOf(failedStepId);
        const currentIndex = stepIds.indexOf(stepId);
        if (currentIndex < failedIndex) return "completed";
        if (currentIndex === failedIndex) return "error";
        return "pending";
      }
    }

    const stepIndex = STEPS.findIndex((s) => s.id === stepId);

    // All steps completed
    if (status.next.length === 0) {
      if (status.values && Object.keys(status.values).length > 0) return "completed";
      return "pending";
    }

    // Current step is active
    if (status.next.includes(stepId)) return "active";

    // Determine based on next step position
    const firstNext = status.next[0];
    const nextIndex = STEPS.findIndex((s) => s.id === firstNext);

    if (nextIndex > stepIndex) return "completed";
    if (nextIndex === -1 && status.next.length === 0) return "completed";

    return "pending";
  };

  const formatElapsedTime = (startTime: string): string => {
    const diff = Math.floor((new Date().getTime() - new Date(startTime).getTime()) / 1000);
    if (diff < 0) return "00:00";
    const m = Math.floor(diff / 60);
    const s = diff % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  // Handlers
  const isSeriesSelected = initialSeriesId ? true : selectedSeriesId !== "" as string;

  const handleStartRun = () => {
    if (!isSeriesSelected) return;

    const seriesIdToSend = initialSeriesId || (selectedSeriesId !== "" ? selectedSeriesId : null);
    props.onStartRun?.(isDryRun, seriesIdToSend)
  };

  // todo ---
  const handleStartStepByStep = () => {
    if (!isSeriesSelected) return;

    const seriesIdToSend = initialSeriesId || (selectedSeriesId !== "" ? selectedSeriesId : null);
    props.onStartRun?.(isDryRun, seriesIdToSend);
  };

  const handleStopRun = () => {
    if (!window.confirm("Are you sure you want to stop this generation?")) return;
    props.onStopRun?.();
  };

  const handleUploadResume = () => {
    if (!props.threadId) return;
    // condition: ถ้ามีหลาย channel -> เปิด modal ให้เลือก / ถ้ามี channel เดียว -> upload เลย
    if (!youtubeChannels || youtubeChannels.length === 0) {
      if (!window.confirm("Are you sure you want to upload this video to YouTube?")) return;
      props.onResumeUpload?.(props.threadId, null); // ****
      return;
    }

    if (youtubeChannels.length === 1) {
      if (!window.confirm(`Upload to "${youtubeChannels[0].channel_name}"?`)) return;
      props.onResumeUpload?.(props.threadId, Number(youtubeChannels[0].id));
      return;
    }

    setShowUploadModal(true);
  };

  const handleRerun = () => {
    if (!props.threadId) return;
    props.onRerun?.(props.threadId);
  };

  const handleEditClick = (item: any) => {
    const voice = hydrateVoicePrompt(item);
    setEditingItem(String(item.id));
    setEditForm({
      headline: item.headline,
      script: voice.script,
      voice_guide: voice.voice_guide,
      voice_script: voice.voice_script,
      image_prompt: item.image_prompt,
    });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditForm({});
  };

  const handleSaveEdit = () => {
    if (!props.threadId || !editingItem) return;
    props.onUpdateItem?.(props.threadId, editingItem, synchronizeVoicePrompt(editForm));
    setEditingItem(null);
    setEditForm({});
  };

  const handleBack = () => props.onNavigateBack?.();

  const handleStepClick = (stepId: string) => {
    setExpandedStep(expandedStep === stepId ? null : stepId);
  };

  // Render step icon based on status
  const renderStepIcon = (stepStatus: StepStatus) => {
    switch (stepStatus) {
      case "completed":
        return <CheckCircle2 size={16} />;
      case "active":
        return <SpinningLoader size={16} />;
      case "stopped":
        return <SquareStop size={16} />
      case "error":
        return <AlertCircle size={16} />;
      default:
        return <Circle size={16} />;
    }
  };

  // Render step badge
  const renderStepBadge = (stepStatus: StepStatus) => {
    switch (stepStatus) {
      case "completed":
        return <Badge variant="success">Success</Badge>;
      case "active":
        return <Badge variant="info">Running</Badge>;
      case "stopped":
        return <Badge variant="stopped">Stopped</Badge>;
      case "error":
        return <Badge variant="error">Error</Badge>;
      default:
        return null;
    }
  };

  // Render
  return (
    <Container id="megen-gen-video-root">
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <MeGenBreadcrumbs items={breadcrumbItems} onNavigate={handleBack} />
        </Box>
        <AiUsageButton
          id="megen-gen-video-ai-usage-btn"
          threadId={props.threadId}
          canViewAiUsage={props.canViewAiUsage}
          onFetchAiUsage={props.onFetchAiUsage}
          onOpen={() => setIsAiUsageOpen(true)}
        />
      </Box>

      {/* Main Card */}
      <Card id="megen-gen-video-card">
        <Header id="megen-gen-video-header">
          <HeaderLeft id="megen-gen-video-header-left">
            {isViewingRun && (
              <BackButton id="megen-gen-video-back-btn" onClick={handleBack} title="Back">
                <ArrowLeft size={20} />
              </BackButton>
            )}
            <Box>
              <Title id="megen-gen-video-title">{isViewingRun ? runName : "New Workflow Run"}</Title>
              <Subtitle id="megen-gen-video-subtitle">Thread ID: {props.threadId || "Waiting to start"}</Subtitle>
            </Box>
          </HeaderLeft>

          <HeaderRight id="megen-gen-video-header-right">
            {/* Show Dry Run checkbox and Start button when not running and not viewing existing run */}
            {!isRunning && !isViewingRun && (
              <>
                {!initialSeriesId && (
                  <SeriesSelect id="megen-gen-video-series-select" size="small">
                    <InputLabel id="series-select-label">Select Series</InputLabel>
                      <Select
                        labelId="series-select-label"
                        value={selectedSeriesId}
                        label="Series"
                        onChange={(e) => setSelectedSeriesId(e.target.value as number || "")}
                      >
                        {seriesList.map((series) => (
                          <MenuItem key={series.id} value={series.id}>
                            {series.name}
                          </MenuItem>
                        ))}
                      </Select>
                  </SeriesSelect>
                )}

                {initialSeriesId && props.seriesName && (
                  <Typography sx={{ fontSize: "0.875rem", color: "#64748b", fontWeight: 500 }}>
                    Series: <strong>{props.seriesName}</strong>
                  </Typography>
                )}
                
                <CheckboxLabel
                  id="megen-gen-video-dry-run-checkbox"
                  control={
                    <Checkbox
                      checked={isDryRun}
                      onChange={(e) => setIsDryRun(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Dry Run"
                />
                <Tooltip title={!isSeriesSelected ? "Please select a series first" : ""}>
                  <span>
                    <PrimaryButton
                      id="megen-gen-video-start-btn"
                      startIcon={<Play size={18} />}
                      onClick={handleStartRun}
                      disabled={!isSeriesSelected}
                    >
                      All Step
                    </PrimaryButton>
                  </span>
                </Tooltip>
                <Tooltip title={!isSeriesSelected ? "Please select a series first" : ""}>
                  <span>
                    <StepByStepButton
                      id="megen-gen-video-step-by-step-btn"
                      startIcon={<Play size={18} />}
                      onClick={handleStartStepByStep}
                      disabled={!isSeriesSelected}
                    >
                      Step By Step
                    </StepByStepButton>
                  </span>
                </Tooltip>
              </>
            )}

            {/* Stopped indicator */}
            {isStopped && (
              <>
                <Box id="megen-gen-video-stopped-indicator" sx={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#d97706" }}>
                  <Pause size={20} />
                  <Typography sx={{ fontWeight: 500 }}>Stopped</Typography>
                </Box>
                <PrimaryButton
                  id="megen-gen-video-rerun-stopped-btn"
                  startIcon={<Play size={18} />}
                  onClick={handleRerun}
                >
                  Rerun
                </PrimaryButton>
              </>
            )}

            {/* Error Indicator + Rerun Button */}
            {isError && !isRunning && (
              <>
                <Box id="megen-gen-video-error-indicator" sx={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#dc2626" }}>
                  <AlertCircle size={20} />
                  <Typography sx={{ fontWeight: 500 }}>Error</Typography>
                </Box>
                <PrimaryButton
                  id="megen-gen-video-rerun-error-btn"
                  startIcon={<Play size={18} />}
                  onClick={handleRerun}
                >
                  Rerun
                </PrimaryButton>
              </>
            )}

            {/* Ready for upload */}
            {status?.next?.includes("upload_to_youtube") && !isStopped && !isError && (
              <>
                <Badge variant="warning">Ready for Upload</Badge>
                <DangerButton id="megen-gen-video-upload-btn" startIcon={<Upload size={18} />} onClick={handleUploadResume}>
                  Upload to YouTube
                </DangerButton>
              </>
            )}

            {/* Running indicator with Stop button */}
            {isRunning && !status?.next?.includes("upload_to_youtube") && !isStopped && (
              <>
                <RunningIndicator id="megen-gen-video-running-indicator">
                  <SpinningLoader size={20} />
                  <Typography sx={{ fontWeight: 500 }}>Running...</Typography>
                </RunningIndicator>
                <DangerButton id="megen-gen-video-stop-btn" startIcon={<StopCircle size={18} />} onClick={handleStopRun}>
                  Stop
                </DangerButton>
              </>
            )}

            {/* Expand/Collapse Button - แสดงเสมอเมื่อมี progress */}
            {status?.progress && Object.keys(status.progress).length > 0 && (
              <IconButton
                id="megen-gen-video-expand-btn"
                onClick={() => setIsProgressExpanded(!isProgressExpanded)}
                size="small"
                sx={{
                  color: "#64748b",
                  backgroundColor: "#f1f5f9",
                  "&:hover": { backgroundColor: "#e2e8f0" },
                  padding: "0.5rem",
                }}
                title={isProgressExpanded ? "Collapse Progress" : "Expand Progress"}
              >
                {isProgressExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </HeaderRight>
        </Header>

        {/* Collapsible Steps Section */}
        <Collapse id="megen-gen-video-steps-collapse" in={isProgressExpanded}>
          <Box id="megen-gen-video-steps-list">
            {STEPS.map((step, idx) => {
              const stepStatus = getStepStatus(step.id);
              const progressData = status?.progress?.[step.id];
              
              // Auto-expand active or stopped step, or manually expanded
              const isExpanded = 
                stepStatus === "active" || 
                stepStatus === "stopped" ||
                stepStatus === "error" ||
                expandedStep === step.id;

              return (
                <Fragment key={step.id}>
                  <StepContainer id={`megen-gen-video-step-${step.id}`} onClick={() => handleStepClick(step.id)}>
                    {idx !== STEPS.length - 1 && <ConnectingLine />}
                    <StepIndicator status={stepStatus}>
                      {renderStepIcon(stepStatus)}
                    </StepIndicator>
                    <StepContent>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <StepLabel status={stepStatus}>{step.label}</StepLabel>
                        {/* Show current detail text for active step */}
                        {stepStatus === "active" && progressData?.detail && (
                          <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontStyle: "italic" }}>
                            — {progressData.detail}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        {/* Duration for completed steps */}
                        {stepStatus === "completed" && status?.values?.step_durations?.[step.id] && (
                          <Typography sx={{ fontSize: "0.75rem", fontFamily: "monospace", color: "#94a3b8" }}>
                            {Number(status.values.step_durations[step.id]).toFixed(2)}s
                          </Typography>
                        )}
                        
                        {/* Elapsed time for active step */}
                        {stepStatus === "active" && status?.created_at && (
                          <Typography sx={{ fontSize: "0.75rem", fontFamily: "monospace", color: "#2563eb" }}>
                            {formatElapsedTime(status.created_at)}
                          </Typography>
                        )}

                        {/* Error message */}
                        {stepStatus === "error" && (
                          <Typography sx={{ fontSize: "0.75rem", color: "#ef4444", fontWeight: 500 }}>
                            {progressData?.error || "Failed"}
                          </Typography>
                        )}

                        {/* Stopped message */}
                        {stepStatus === "stopped" && (
                          <Typography sx={{ fontSize: "0.75rem", color: "#d97706", fontWeight: 500 }}>
                            {STOPPED_BY_USER}
                          </Typography>
                        )}

                        {/* Badge */}
                        {renderStepBadge(stepStatus)}

                        {/* Stop button for active step */}
                        {stepStatus === "active" && isRunning && (
                          <StopButton 
                            size="small" 
                            onClick={(e) => { e.stopPropagation(); handleStopRun(); }}
                            title="Stop generation"
                          >
                            <StopCircle size={14} />
                          </StopButton>
                        )}
                      </Box>
                    </StepContent>
                  </StepContainer>

                  {/* Progress Detail - Auto-expand for active/stopped or manual expand */}
                  {isExpanded && progressData && (
                    <ProgressDetail>
                      {/* Progress bar */}
                      {progressData.total > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "#64748b", mb: 0.5 }}>
                            <span>
                              {stepStatus === "stopped" ? "Stopped at item" : "Processing items..."}
                            </span>
                            <span>{progressData.current} / {progressData.total}</span>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(progressData.current / progressData.total) * 100}
                            sx={{ 
                              height: 6, 
                              borderRadius: 3,
                              backgroundColor: stepStatus === "stopped" ? "#fef3c7" : undefined,
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: stepStatus === "stopped" ? "#f59e0b" : undefined,
                              }
                            }}
                          />
                        </Box>
                      )}

                      {/* Current item being processed */}
                      {progressData.current_item && stepStatus === "active" && (
                        <Typography sx={{ fontSize: "0.75rem", color: "#64748b", mb: 0.5 }}>
                          Current: <strong>{progressData.current_item}</strong>
                        </Typography>
                      )}

                      {/* Logs */}
                      {progressData.logs && progressData.logs.length > 0 && (
                        // column-reverse + reverse array: ลำดับที่เห็นยังเป็น เก่า→ใหม่ แต่ scroll ปักที่บรรทัดล่าสุดขณะ log ไหลเข้า
                        <Box sx={{ mt: 1, maxHeight: "150px", overflowY: "auto", display: "flex", flexDirection: "column-reverse" }}>
                          {[...progressData.logs].reverse().map((log: string, i: number, arr: string[]) => (
                            <Typography
                              key={arr.length - 1 - i}
                              sx={{
                                fontSize: "0.75rem", 
                                fontFamily: "monospace", 
                                color: "#64748b",
                                lineHeight: 1.5,
                              }}
                            >
                              {log}
                            </Typography>
                          ))}
                        </Box>
                      )}

                      {/* Empty state */}
                      {progressData && !progressData.logs?.length && !progressData.current_item && !((progressData.total || 0) > 0) && (
                        <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontStyle: "italic" }}>
                          No detail available
                        </Typography>
                      )}
                    </ProgressDetail>
                  )}
                </Fragment>
              );
            })}
          </Box>
        </Collapse>

        {/* Summary เมื่อ Collapsed (Optional) */}
        {!isProgressExpanded && status?.progress && (
          <Box
            sx={{
              padding: "1rem",
              backgroundColor: "#f8fafc",
              borderRadius: "0.5rem",
              border: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
              {STEPS.filter((s) => getStepStatus(s.id) === "completed").length} / {STEPS.length} steps completed
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontStyle: "italic" }}>
              Click expand to view details
            </Typography>
          </Box>
        )}
      </Card>

      {/* ... rest of the cards (Thumbnail, Assets, Video, Logs) */}
      {/* Thumbnail & Metadata */}
      {thumbnailData.thumbnailPath && (
        <CardGenerateVideoThumbnail
          thumbnailPath={thumbnailData.thumbnailPath}
          thumbnailPrompt={thumbnailData.thumbnailPrompt}
          youtubeTitle={thumbnailData.youtubeTitle}
          youtubeDescription={thumbnailData.youtubeDescription}
          thumbnailDate={thumbnailData.thumbnailDate}
          onSaveMetadata={handleSaveMetadata}
          mediaVersion={mediaVersion}
          isRunning={isRunning}
        />
      )}

      {/* Assets Viewer */}
      {statbleItems.length > 0 && (
        <CardGenerateVideoAssets
          items={statbleItems}
          onUpdateItem={(threadId, itemId, data) => {
            props.onUpdateItem?.(threadId, itemId, data, handlePollingSuccess);
          }}
          onUpdateComposition={handleSaveComposition}
          threadId={props.threadId}
          mediaVersion={assetsMediaVersion}
          isRunning={isAssetsRunning}
        />
      )}

      {/* Final Video */}
      {!isRunning && finalVideoUrl && (
        <CardGenerateVideoFinalVideo videoUrl={finalVideoUrl} />
      )}

      {/* Live Output / Logs */}
      {status?.values && (
        <TerminalCard id="megen-gen-video-terminal">
          <TerminalHeader id="megen-gen-video-terminal-header">
            <TerminalDot color="#ef4444" />
            <TerminalDot color="#eab308" />
            <TerminalDot color="#22c55e" />
            <span style={{ marginLeft: "0.5rem" }}>Live Output</span>
          </TerminalHeader>
          <pre style={{ whiteSpace: "pre-wrap", maxHeight: "15rem", overflow: "auto", margin: 0 }}>
            {JSON.stringify(status.values, null, 2)}
          </pre>
        </TerminalCard>
      )}

      {/* Upload To YouTube Mod */}
      {showUploadModal && (
        <Box
          id="megen-gen-video-upload-modal-overlay"
          sx={{
            position: "fixed", inset: 0, zIndex: 50,
            display: "flex", alignItems: "center", justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
          onClick={() => setShowUploadModal(false)}
        >
          <Box
            id="megen-gen-video-upload-modal"
            sx={{
              backgroundColor: "white", borderRadius: "0.75rem",
              padding: "1.5rem", minWidth: "320px", maxWidth: "420px", width: "100%",
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <Box id="megen-gen-video-upload-modal-header" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <YouTubeIcon sx={{ color: "#e91e1e", fontSize: "1.5rem" }} />
                <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#1e293b" }}>
                  Select YouTube Channel
                </Typography>
              </Box>
              <IconButton id="megen-gen-video-upload-modal-close-btn" size="small" onClick={() => setShowUploadModal(false)}>
                <CloseIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </Box>

            <Typography sx={{ fontSize: "0.8rem", color: "#64748b", mb: 1.5 }}>
              Choose which channel to upload this video to:
            </Typography>

            {/* Channel List */}
            <Box id="megen-gen-video-upload-modal-channel-list" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {youtubeChannels?.map(ch => (
                <Box
                  id={`megen-gen-video-upload-channel-${ch.id}`}
                  key={ch.id}
                  onClick={() => {
                    setShowUploadModal(false);
                    props.onResumeUpload?.(props.threadId!, ch._id);
                  }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    padding: "0.75rem 1rem",
                    border: "1px solid #e2e8f0", borderRadius: "0.5rem",
                    cursor: ch.status === "connected" ? "pointer" : "not-allowed",
                    opacity: ch.status === "connected" ? 1 : 0.5,
                    "&:hover": ch.status === "connected"
                      ? { backgroundColor: "#f8fafc", borderColor: "#cbd5e1" }
                      : {},
                  }}
                >
                  <YouTubeIcon sx={{ color: "#e91e1e", fontSize: "1.25rem", flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b" }}>
                      {ch.channel_name || "Unknown Channel"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: ch.status === "connected" ? "#22c55e" : "#f59e0b" }}>
                      {ch.status}
                    </Typography>
                  </Box>
                  {ch.status !== "connected" && (
                    <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8" }}>Unavailable</Typography>
                  )}
                </Box>
              ))}
            </Box>

            {/* Cancel */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                id="megen-gen-video-upload-modal-cancel-btn"
                onClick={() => setShowUploadModal(false)}
                sx={{ textTransform: "none", color: "#64748b", fontSize: "0.875rem" }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    
      {/* --- AI Usage (See more) --- */}
      <ModMeGenAiUsage
        open={isAiUsageOpen}
        threadId={props.threadId}
        userNameMap={props.aiUsageUserNameMap}
        threadTitle={isViewingRun ? runName : undefined}
        onClose={() => setIsAiUsageOpen(false)}
        onFetchUsage={props.onFetchAiUsage}
      />
    </Container>
  );
};

CardGenerateVideo.displayName = "CardGenerateVideo";

export default memo(CardGenerateVideo);
